'use client'

import React from 'react'
import { Annotation, Annotation2 } from '@/types/annotation'
import { AnimatedMarkdownMusic } from '@/components/messages/animated-markdown-music'

// Utility: pick wrapped result if present
function unwrap<T = any>(maybeWrapped: any): T | undefined {
  if (!maybeWrapped) return undefined
  return typeof maybeWrapped === 'object' && 'result' in maybeWrapped
    ? (maybeWrapped as any).result
    : maybeWrapped
}

function findAudioUrl(obj: any): string | undefined {
  if (!obj) return undefined
  const audioExt = ['.mp3', '.wav', '.m4a', '.flac', '.ogg']

  // direct string url
  if (typeof obj === 'string') {
    const s = obj.toLowerCase()
    if (s.startsWith('http')) {
      if (audioExt.some((ext) => s.includes(ext))) return obj
    }
    return undefined
  }

  // object: scan shallow values and arrays
  if (typeof obj === 'object') {
    for (const key of Object.keys(obj)) {
      const val = (obj as any)[key]
      if (typeof val === 'string') {
        const url = findAudioUrl(val)
        if (url) return url
      } else if (Array.isArray(val)) {
        for (const it of val) {
          const url = findAudioUrl(it)
          if (url) return url
        }
      } else if (val && typeof val === 'object') {
        const url = findAudioUrl(val)
        if (url) return url
      }
    }
  }
  return undefined
}

export function AudioToolAnnotation({ annotation }: { annotation: Annotation | Annotation2 }) {
  const raw = (annotation as any)['audioTool__processAudio']
  if (!raw)
    return (
      <div className="flex flex-col gap-3">
        <div className="bg-muted/40 h-12 w-full rounded-md motion-safe:animate-pulse motion-reduce:animate-none" />
        <div className="space-y-2">
          <div className="bg-muted/40 h-4 w-5/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="bg-muted/30 h-4 w-4/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="bg-muted/20 h-4 w-3/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
        </div>
      </div>
    )

  const result = unwrap<any>(raw)
  if (!result)
    return (
      <div className="flex flex-col gap-3">
        <div className="bg-muted/40 h-12 w-full rounded-md motion-safe:animate-pulse motion-reduce:animate-none" />
        <div className="space-y-2">
          <div className="bg-muted/40 h-4 w-5/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="bg-muted/30 h-4 w-4/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="bg-muted/20 h-4 w-3/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
        </div>
      </div>
    )

  const transcript: string | undefined = typeof result.text === 'string' ? result.text : undefined
  const audioUrl: string | undefined = findAudioUrl(result)
  const title: string =
    typeof result.title === 'string' && result.title.trim()
      ? result.title.trim()
      : result.mode === 'generate_voiceover'
        ? 'Generated voiceover'
        : 'Generated or source audio'

  const isEmpty = !audioUrl && !transcript

  return (
    <div className="flex flex-col gap-3">
      {/* Loading skeleton when no outputs yet */}
      {isEmpty && (
        <div className="flex flex-col gap-3">
          <div className="bg-muted/40 h-12 w-full rounded-md motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="space-y-2">
            <div className="bg-muted/40 h-4 w-5/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="bg-muted/30 h-4 w-4/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="bg-muted/20 h-4 w-3/6 rounded motion-safe:animate-pulse motion-reduce:animate-none" />
          </div>
        </div>
      )}

      {audioUrl && (
        <AnimatedMarkdownMusic src={audioUrl} alt={title} isStreaming={false} />
      )}
      {transcript && (
        <div className="bg-muted/30 rounded-md border p-3 text-sm">
          <div className="mb-1 font-medium">Transcript</div>
          <div className="text-muted-foreground whitespace-pre-wrap break-words">{transcript}</div>
        </div>
      )}
    </div>
  )
}

export default AudioToolAnnotation
